import * as yup from "yup";
import {
  B,
  byte,
  EB,
  GB,
  K,
  KB,
  M,
  MB,
  PB,
  simpleNumber,
  TB,
} from "@shared/constants/constants";
import { DateTime } from "luxon";

export enum UnitFamily {
  DATA = "DATA",
  TIME = "TIME",
  SIMPLE_NUMBER = "SIMPLE_NUMBER",
}
export enum UsageUnitSymbol {
  NONE = "",
  K = "K",
  M = "M",
  G = "G",
  T = "T",
  P = "P",
  E = "E",
}

export enum NumberUsageUnitSymbol {
  NONE = "",
  K = "K",
  M = "M",
  B = "B",
}

const usageSymbolLookup: { value: number; symbol: UsageUnitSymbol }[] = [
  { value: byte, symbol: UsageUnitSymbol.NONE },
  { value: KB, symbol: UsageUnitSymbol.K },
  { value: MB, symbol: UsageUnitSymbol.M },
  { value: GB, symbol: UsageUnitSymbol.G },
  { value: TB, symbol: UsageUnitSymbol.T },
  { value: PB, symbol: UsageUnitSymbol.P },
  { value: EB, symbol: UsageUnitSymbol.E },
];

const numberSymbolLookup: { value: number; symbol: NumberUsageUnitSymbol }[] = [
  { value: simpleNumber, symbol: NumberUsageUnitSymbol.NONE },
  { value: K, symbol: NumberUsageUnitSymbol.K },
  { value: M, symbol: NumberUsageUnitSymbol.M },
  { value: B, symbol: NumberUsageUnitSymbol.B },
];

const myJFrogUtils = {
  sleep: async (seconds: number) => {
    await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  },
  byteToGigaByte: (num: number) => {
    const gigaBytes = +(num / Math.pow(1024, 3)).toFixed(2);
    return isFinite(gigaBytes) ? gigaBytes : 0;
  },
  buildNumYupField: (
    label: string,
    fieldErrorName: string,
    when?: string[] | string,
    is?: (...args: number[]) => boolean,
  ): yup.AnySchema => {
    const numYup = yup
      .number()
      .nullable()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0)
      .max(
        10 ** 14,
        `The ${fieldErrorName} field may not be greater than 15 characters`,
      )
      .transform((value) => (!value ? null : value))
      .label(label);

    if (when) {
      return numYup.when(when, {
        is,
        then: () =>
          yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .required(`Please fill one of the fields`),
      });
    }

    return numYup.required();
  },
  isDateValid: (date: Date) => {
    return Object.prototype.toString.call(date) === "[object Date]";
  },
  randomNum: (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min) + min);
  },
  generateUniqueName: (): string =>
    Math.random().toString(36).slice(2) + Date.now().toString(36),
  generateUUIDv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0,
        value = char === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  },
  generateCharString: (length: number) => {
    const charSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    return Array.from(
      { length },
      () => charSet[Math.floor(Math.random() * charSet.length)],
    ).join("");
  },
  roundNumber({ value, decimals }: { value: number; decimals: number }) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  },
  getHighestSymbol: (symbols: Set<UsageUnitSymbol>): UsageUnitSymbol | null => {
    const orderedSymbols = usageSymbolLookup.map((item) => item.symbol);
    for (let i = orderedSymbols.length - 1; i >= 0; i--) {
      if (symbols.has(orderedSymbols[i])) {
        return orderedSymbols[i];
      }
    }
    return null;
  },
  findSymbolLookupForUsageNumber: (num: number) => {
    return usageSymbolLookup
      .slice()
      .reverse()
      .find(function (item) {
        return num >= item.value;
      });
  },
  findSymbolLookupForSimpleNumber: (num: number) => {
    return numberSymbolLookup
      .slice()
      .reverse()
      .find(function (item) {
        return num >= item.value;
      });
  },
  shortenValueWithSymbol: (
    num: number,
    digits: number,
    config?: {
      hideSymbol?: boolean;
      addBytesToSymbolLabel?: boolean;
      addSpaceBeforeUnit?: boolean;
      forceSymbol?: UsageUnitSymbol | NumberUsageUnitSymbol;
      isSimpleNumbers?: boolean;
    },
  ) => {
    const valueNumber = config?.isSimpleNumbers
      ? myJFrogUtils.findSymbolLookupForSimpleNumber(num)
      : myJFrogUtils.findSymbolLookupForUsageNumber(num);

    const forcedSymbol = config?.isSimpleNumbers
      ? numberSymbolLookup.find((item) => item.symbol === config?.forceSymbol)
      : usageSymbolLookup.find((item) => item.symbol === config?.forceSymbol);

    const symbolLookupItem = config?.forceSymbol ? forcedSymbol : valueNumber;

    const symbol = config?.hideSymbol ? "" : symbolLookupItem?.symbol || "";

    const configurableSpaceBeforeUnit =
      (config?.addSpaceBeforeUnit && " ") || "";
    const configurableByteSymbol = (config?.addBytesToSymbolLabel && "B") || "";

    return symbolLookupItem
      ? parseFloat((num / symbolLookupItem.value).toFixed(digits)) +
          `${configurableSpaceBeforeUnit}${symbol}${configurableByteSymbol}`
      : `0${configurableSpaceBeforeUnit}${configurableByteSymbol}`;
  },
  formatCurrency(amount: number | null | undefined) {
    if (amount == null) return "$0.00";
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },
  formatNumberWithCommas(number: number, nbDecimals = 0) {
    return number.toLocaleString(navigator.language, {
      minimumFractionDigits: nbDecimals,
      maximumFractionDigits: nbDecimals,
    });
  },
  formatNumberToEnUs(number: number, nbDecimals: number) {
    if (Number.isInteger(number)) {
      return number.toLocaleString("en-US");
    }
    return number.toLocaleString("en-US", {
      minimumFractionDigits: nbDecimals,
      maximumFractionDigits: nbDecimals,
    });
  },

  exportCsvContent(
    data: string,
    fileName: string,
    startDate: number,
    endDate: number,
  ) {
    const startDateStr = DateTime.fromMillis(startDate)
      .toUTC()
      .toFormat("yyyyMMdd");
    const endDateStr = DateTime.fromMillis(endDate)
      .toUTC()
      .toFormat("yyyyMMdd");

    const filename = `${fileName}-${startDateStr}-${endDateStr}.csv`;
    const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  calculatePercentage: (value: number, total: number): number => {
    if (total === 0) {
      return 0;
    }
    let percentage = parseInt(((value / total) * 100).toFixed(0));
    if (percentage === 100) {
      if (value < total) {
        percentage = 99;
      }
      if (value > total) {
        percentage = 101;
      }
    }
    return percentage;
  },
  calculateOverPercentage: (value: number, total: number) => {
    const percent = myJFrogUtils.calculatePercentage(value, total);
    const absolutePercentage = Math.abs(percent);
    if (absolutePercentage === 100 || percent === 0) {
      return 1;
    }
    return Math.abs(percent - 100).toLocaleString();
  },
};

export default myJFrogUtils;
